import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Activity,
  AlertCircle,
  BarChart3,
  Bell,
  Calendar,
  Clock,
  Cpu,
  Database,
  Download,
  FileUp,
  Globe,
  HardDrive,
  Image,
  Key,
  RefreshCw,
  Search,
  TrendingUp,
  Upload,
  Video,
  Wallet,
} from 'lucide-react';
import {
  apiService,
  AuthUsage,
  BillingAccount,
  BillingBudgetAlertsResponse,
  CurrentUsageResponse,
  UsageBreakdownResponse,
  UsageEvent,
  UsageHistoryResponse,
  UserSettings,
} from '../services/api';
import { commitOwnedSearch, HistoryMode } from '../navigation/historyState';
import { parseEnumParam, parseIntegerParam } from '../navigation/urlCodec';
import { formatCredits } from '../utils/credits';
import CustomSelect from './CustomSelect';
import { useTabActivity } from '../contexts/TabActivityContext';
import './UsageTab.css';

type DateRange = '7d' | '30d' | '90d' | 'all';
type SortBy = 'timestamp' | 'value' | 'metric_type';
type SortDir = 'asc' | 'desc';

type MetricConfig = {
  label: string;
  icon: React.ReactNode;
  color: string;
  format: (value: number) => string;
  enterpriseOnly?: boolean;
};

const formatInteger = (value: number) => Math.round(value || 0).toLocaleString();
const formatMinutes = (value: number) => `${(value || 0).toFixed(1)} min`;
const formatCompact = (value: number) => {
  if (value >= 1000000) return `${(value / 1000000).toFixed(2)}M`;
  if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
  return formatInteger(value);
};
const formatStorage = (value: number) => {
  if (value >= 1073741824) return `${(value / 1073741824).toFixed(2)} GB`;
  if (value >= 1048576) return `${(value / 1048576).toFixed(2)} MB`;
  if (value >= 1024) return `${(value / 1024).toFixed(2)} KB`;
  return `${formatInteger(value)} B`;
};

const METRIC_CONFIG: Record<string, MetricConfig> = {
  videos_uploaded: {
    label: 'Media Uploaded',
    icon: <Upload size={20} />,
    color: '#3b82f6',
    format: formatInteger,
  },
  videos_processed: {
    label: 'Media Processed',
    icon: <Video size={20} />,
    color: '#10b981',
    format: formatInteger,
  },
  video_minutes: {
    label: 'Media Minutes',
    icon: <Clock size={20} />,
    color: '#22c55e',
    format: formatMinutes,
  },
  segments_created: {
    label: 'Segments',
    icon: <Database size={20} />,
    color: '#8b5cf6',
    format: formatInteger,
  },
  search_queries: {
    label: 'Text Searches',
    icon: <Search size={20} />,
    color: '#f59e0b',
    format: formatInteger,
  },
  image_search_queries: {
    label: 'Image Searches',
    icon: <Image size={20} />,
    color: '#ec4899',
    format: formatInteger,
  },
  storage_bytes: {
    label: 'Storage Added',
    icon: <HardDrive size={20} />,
    color: '#14b8a6',
    format: formatStorage,
  },
  export_count: {
    label: 'Exports',
    icon: <Download size={20} />,
    color: '#f97316',
    format: formatInteger,
  },
  import_files: {
    label: 'Files Imported',
    icon: <FileUp size={20} />,
    color: '#84cc16',
    format: formatInteger,
  },
  embedding_operations: {
    label: 'Text Embeddings',
    icon: <TrendingUp size={20} />,
    color: '#0ea5e9',
    format: formatInteger,
  },
  image_embedding_operations: {
    label: 'Image Embeddings',
    icon: <Image size={20} />,
    color: '#f472b6',
    format: formatInteger,
  },
  llm_input_tokens: {
    label: 'LLM Input Tokens',
    icon: <Cpu size={20} />,
    color: '#6366f1',
    format: formatCompact,
    enterpriseOnly: true,
  },
  llm_output_tokens: {
    label: 'LLM Output Tokens',
    icon: <Cpu size={20} />,
    color: '#a855f7',
    format: formatCompact,
    enterpriseOnly: true,
  },
};

const OPERATIONAL_METRICS = [
  'videos_uploaded',
  'videos_processed',
  'video_minutes',
  'storage_bytes',
  'search_queries',
  'image_search_queries',
  'segments_created',
  'embedding_operations',
  'image_embedding_operations',
  'import_files',
  'export_count',
];

const RANGE_OPTIONS = ['7d', '30d', '90d', 'all'] as const;
const SORT_OPTIONS = ['timestamp', 'value', 'metric_type'] as const;
const SORT_DIR_OPTIONS = ['asc', 'desc'] as const;
const OWNED_KEYS = ['range', 'months', 'metric', 'esort', 'edir', 'eq'];

const getMetricConfig = (metricType: string): MetricConfig => (
  METRIC_CONFIG[metricType] || {
    label: metricType.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
    icon: <Activity size={20} />,
    color: '#6b7280',
    format: formatInteger,
  }
);

const isEnterprisePlan = (account: BillingAccount | null) => (
  String(account?.plan_id || '').trim().toLowerCase() === 'enterprise'
);

const UsageTab: React.FC = () => {
  const [account, setAccount] = useState<BillingAccount | null>(null);
  const [currentUsage, setCurrentUsage] = useState<CurrentUsageResponse | null>(null);
  const [usageHistory, setUsageHistory] = useState<UsageHistoryResponse | null>(null);
  const [usageBreakdown, setUsageBreakdown] = useState<UsageBreakdownResponse | null>(null);
  const [usageEvents, setUsageEvents] = useState<UsageEvent[]>([]);
  const [budgetAlerts, setBudgetAlerts] = useState<BillingBudgetAlertsResponse | null>(null);
  const [userSettings, setUserSettings] = useState<UserSettings | null>(null);

  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [dateRange, setDateRange] = useState<DateRange>('30d');
  const [historyMonths, setHistoryMonths] = useState(6);
  const [eventMetricFilter, setEventMetricFilter] = useState('');
  const [eventSortBy, setEventSortBy] = useState<SortBy>('timestamp');
  const [eventSortOrder, setEventSortOrder] = useState<SortDir>('desc');
  const [eventSearchTerm, setEventSearchTerm] = useState('');
  const [urlHydrated, setUrlHydrated] = useState(false);

  const [budgetMonthlyCredits, setBudgetMonthlyCredits] = useState('');
  const [budgetAlertPercents, setBudgetAlertPercents] = useState<number[]>([]);
  const [budgetNewPercent, setBudgetNewPercent] = useState('');
  const [budgetDirty, setBudgetDirty] = useState(false);
  const [budgetSaving, setBudgetSaving] = useState(false);
  const [budgetError, setBudgetError] = useState<string | null>(null);
  const [budgetSuccess, setBudgetSuccess] = useState<string | null>(null);

  const historyModeRef = useRef<HistoryMode>('replace');
  const isApplyingUrlRef = useRef(false);
  const budgetDirtyRef = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { isActive } = useTabActivity();

  const isEnterprise = isEnterprisePlan(account);

  useEffect(() => {
    budgetDirtyRef.current = budgetDirty;
  }, [budgetDirty]);

  useEffect(() => {
    isApplyingUrlRef.current = true;
    const params = new URLSearchParams(location.search);
    setDateRange(parseEnumParam(params.get('range'), RANGE_OPTIONS, '30d'));
    setHistoryMonths(parseIntegerParam(params.get('months'), 6, 1, 36));
    setEventMetricFilter(params.get('metric') || '');
    setEventSortBy(parseEnumParam(params.get('esort'), SORT_OPTIONS, 'timestamp'));
    setEventSortOrder(parseEnumParam(params.get('edir'), SORT_DIR_OPTIONS, 'desc'));
    setEventSearchTerm(params.get('eq') || '');

    const timer = window.setTimeout(() => {
      isApplyingUrlRef.current = false;
      setUrlHydrated(true);
    }, 0);

    return () => window.clearTimeout(timer);
  }, [location.search]);

  useEffect(() => {
    if (!isActive || !urlHydrated || isApplyingUrlRef.current) return;
    const mode = historyModeRef.current;
    historyModeRef.current = 'replace';
    commitOwnedSearch({
      location,
      navigate,
      ownedKeys: OWNED_KEYS,
      updates: {
        range: dateRange,
        months: historyMonths,
        metric: isEnterprise && eventMetricFilter ? eventMetricFilter : null,
        esort: isEnterprise ? eventSortBy : null,
        edir: isEnterprise ? eventSortOrder : null,
        eq: isEnterprise && eventSearchTerm ? eventSearchTerm : null,
      },
      mode,
    });
  }, [
    dateRange,
    isActive,
    eventMetricFilter,
    eventSearchTerm,
    eventSortBy,
    eventSortOrder,
    historyMonths,
    isEnterprise,
    location,
    navigate,
    urlHydrated,
  ]);

  const getDateRangeValues = useCallback(() => {
    const now = new Date();
    let startDate: string | undefined;
    let endDate: string | undefined = now.toISOString();

    if (dateRange === '7d') startDate = new Date(now.getTime() - 7 * 86400000).toISOString();
    if (dateRange === '30d') startDate = new Date(now.getTime() - 30 * 86400000).toISOString();
    if (dateRange === '90d') startDate = new Date(now.getTime() - 90 * 86400000).toISOString();
    if (dateRange === 'all') {
      startDate = undefined;
      endDate = undefined;
    }

    return { startDate, endDate };
  }, [dateRange]);

  const hydrateBudgetForm = useCallback((data: BillingBudgetAlertsResponse) => {
    if (budgetDirtyRef.current) return;
    setBudgetMonthlyCredits(data.monthly_budget_mt && data.monthly_budget_mt > 0 ? String(data.monthly_budget_mt) : '');
    setBudgetAlertPercents(Array.isArray(data.alert_percents) ? data.alert_percents : []);
    setBudgetError(null);
  }, []);

  const loadUsageEvents = useCallback(async (enterprise: boolean) => {
    if (!enterprise) {
      setUsageEvents([]);
      return;
    }
    const { startDate, endDate } = getDateRangeValues();
    const data = await apiService.getUsageDetails(
      eventMetricFilter || undefined,
      startDate,
      endDate,
      500,
    );
    setUsageEvents(data);
  }, [eventMetricFilter, getDateRangeValues]);

  const loadData = useCallback(async ({ refreshing = false } = {}) => {
    if (refreshing) setIsRefreshing(true);
    else setIsLoading(true);
    setLoadError(null);

    try {
      const { startDate, endDate } = getDateRangeValues();
      const accountData = await apiService.getBillingAccount();
      const currentData = await apiService.getCurrentUsage();
      const historyData = await apiService.getUsageHistory(historyMonths);
      const breakdownData = await apiService.getUsageBreakdown(startDate, endDate);
      const budgetData = await apiService.getBillingBudgetAlerts();
      const settingsData = await apiService.getUserSettings();

      setAccount(accountData);
      setCurrentUsage(currentData);
      setUsageHistory(historyData);
      setUsageBreakdown(breakdownData);
      setBudgetAlerts(budgetData);
      setUserSettings(settingsData);
      hydrateBudgetForm(budgetData);
      await loadUsageEvents(isEnterprisePlan(accountData));
    } catch (error: any) {
      console.error('Failed to load usage data:', error);
      setLoadError(error?.response?.data?.detail || 'Failed to load usage data');
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [getDateRangeValues, historyMonths, hydrateBudgetForm, loadUsageEvents]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleRangeChange = (value: string) => {
    historyModeRef.current = 'push';
    setDateRange(value as DateRange);
  };

  const handleHistoryMonthsChange = (value: string) => {
    historyModeRef.current = 'push';
    setHistoryMonths(parseInt(value, 10));
  };

  const handleRefresh = () => loadData({ refreshing: true });

  const budgetingDisabled = useMemo(() => {
    const parsed = parseFloat(budgetMonthlyCredits);
    return !budgetMonthlyCredits.trim() || Number.isNaN(parsed) || parsed <= 0;
  }, [budgetMonthlyCredits]);

  const addBudgetThreshold = (value: number) => {
    if (!Number.isFinite(value)) return;
    const percent = Math.max(1, Math.min(100, Math.round(value)));
    setBudgetAlertPercents((prev) => Array.from(new Set([...prev, percent])).sort((a, b) => a - b).slice(0, 10));
    setBudgetDirty(true);
  };

  const saveBudgetAlerts = async () => {
    setBudgetSaving(true);
    setBudgetError(null);
    setBudgetSuccess(null);
    try {
      const parsedBudget = budgetMonthlyCredits.trim() ? parseFloat(budgetMonthlyCredits) : NaN;
      const monthly_budget_mt =
        budgetMonthlyCredits.trim() && Number.isFinite(parsedBudget) && parsedBudget > 0 ? parsedBudget : null;
      const resp = await apiService.updateBillingBudgetAlerts({
        monthly_budget_mt,
        alert_percents: budgetingDisabled ? [] : budgetAlertPercents,
      });
      setBudgetAlerts(resp);
      setBudgetMonthlyCredits(resp.monthly_budget_mt && resp.monthly_budget_mt > 0 ? String(resp.monthly_budget_mt) : '');
      setBudgetAlertPercents(Array.isArray(resp.alert_percents) ? resp.alert_percents : []);
      setBudgetDirty(false);
      setBudgetSuccess('Saved');
      window.setTimeout(() => setBudgetSuccess(null), 2000);
    } catch (error: any) {
      console.error('Failed to update budget alerts:', error);
      setBudgetError(error?.response?.data?.detail || 'Failed to save budget alerts');
    } finally {
      setBudgetSaving(false);
    }
  };

  const enableEmailNotifications = async () => {
    try {
      const updated = await apiService.updateUserSettings({ notification_email: true });
      setUserSettings(updated);
    } catch (error) {
      console.error('Failed to enable email notifications:', error);
    }
  };

  const formatDate = (dateStr?: string | null) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatDateTime = (dateStr: string) => new Date(dateStr).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  const breakdownMetrics = usageBreakdown?.breakdown || {};
  const currentMetrics = currentUsage?.metrics || {};
  const remainingCredits = Number(account?.balance_mt || 0);
  const usedCredits = Number(budgetAlerts?.consumed_mt || 0);
  const creditTotal = Math.max(0, usedCredits + remainingCredits);
  const usedPercent = creditTotal > 0 ? Math.min(100, Math.max(0, (usedCredits / creditTotal) * 100)) : 0;
  const periodLabel = currentUsage ? `${formatDate(currentUsage.period_start)} - ${formatDate(currentUsage.period_end)}` : '';
  const totalSearches = Number(breakdownMetrics.search_queries || 0) + Number(breakdownMetrics.image_search_queries || 0);
  const totalTokens = Number(currentMetrics.llm_input_tokens || 0) + Number(currentMetrics.llm_output_tokens || 0);

  const operationalCards = OPERATIONAL_METRICS.map((metricKey) => ({
    key: metricKey,
    value: Number(breakdownMetrics[metricKey] || 0),
    config: getMetricConfig(metricKey),
  }));

  const filteredEvents = useMemo(() => {
    const term = eventSearchTerm.trim().toLowerCase();
    return [...usageEvents]
      .filter((event) => {
        if (!term) return true;
        return (
          event.metric_type.toLowerCase().includes(term)
          || JSON.stringify(event.metadata || {}).toLowerCase().includes(term)
        );
      })
      .sort((a, b) => {
        let comparison = 0;
        if (eventSortBy === 'timestamp') comparison = new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
        if (eventSortBy === 'value') comparison = a.value - b.value;
        if (eventSortBy === 'metric_type') comparison = a.metric_type.localeCompare(b.metric_type);
        return eventSortOrder === 'asc' ? comparison : -comparison;
      });
  }, [eventSearchTerm, eventSortBy, eventSortOrder, usageEvents]);

  const eventMetricOptions = useMemo(() => [
    { value: '', label: 'All Metrics' },
    ...Object.entries(METRIC_CONFIG).map(([key, config]) => ({ value: key, label: config.label })),
  ], []);

  if (isLoading) {
    return (
      <div className="usage-tab">
        <div className="usage-container">
          <div className="usage-state">
            <RefreshCw size={24} className="spinning" />
            <span>Loading usage data...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="usage-tab">
      <div className="usage-container">
        <div className="usage-section">
          <div className="section-header">
            <div className="section-title">
              <BarChart3 size={20} />
              <h2>Usage Dashboard</h2>
              {account && (
                <span className={`usage-plan-badge ${isEnterprise ? 'enterprise' : ''}`}>
                  {isEnterprise ? 'Enterprise' : 'Standard'}
                </span>
              )}
              {periodLabel && <span className="period-badge">{periodLabel}</span>}
            </div>
            <div className="section-controls">
              <CustomSelect
                value={dateRange}
                onChange={handleRangeChange}
                options={[
                  { value: '7d', label: 'Last 7 Days' },
                  { value: '30d', label: 'Last 30 Days' },
                  { value: '90d', label: 'Last 90 Days' },
                  { value: 'all', label: 'All Time' },
                ]}
              />
              <button className="refresh-btn" onClick={handleRefresh} disabled={isRefreshing} title="Refresh usage data">
                <RefreshCw size={16} className={isRefreshing ? 'spinning' : ''} />
              </button>
            </div>
          </div>

          {loadError && (
            <div className="usage-inline-error">
              <AlertCircle size={16} />
              <span>{loadError}</span>
            </div>
          )}

          <div className="usage-content">
            <section className="credit-overview">
              <div className="credit-overview-main">
                <div className="credit-overview-icon">
                  <Wallet size={28} />
                </div>
                <div>
                  <div className="credit-label">Remaining Credits</div>
                  <div className="credit-value">{formatCredits(remainingCredits)}</div>
                </div>
              </div>
              <div className="credit-progress-block">
                <div className="credit-progress-meta">
                  <span>{formatCredits(usedCredits)} used this month</span>
                  <span>{formatCredits(remainingCredits)} remaining</span>
                </div>
                <div className="credit-progress-track" aria-label="Credit usage progress">
                  <div className="credit-progress-used" style={{ width: `${usedPercent}%` }} />
                </div>
              </div>
              <div className="credit-overview-stats">
                <div>
                  <span>Monthly Used</span>
                  <strong>{formatCredits(usedCredits)}</strong>
                </div>
                <div>
                  <span>Range Searches</span>
                  <strong>{formatInteger(totalSearches)}</strong>
                </div>
                <div>
                  <span>Range Storage Added</span>
                  <strong>{formatStorage(Number(breakdownMetrics.storage_bytes || 0))}</strong>
                </div>
              </div>
            </section>

            <section className="usage-panel">
              <div className="usage-panel-header">
                <h3>
                  <Activity size={18} />
                  Operational Usage
                </h3>
                <span>{dateRange === 'all' ? 'All time' : `Selected range`}</span>
              </div>
              <div className="metric-grid">
                {operationalCards.map(({ key, value, config }) => (
                  <div key={key} className="metric-card">
                    <div className="metric-icon" style={{ backgroundColor: `${config.color}20`, color: config.color }}>
                      {config.icon}
                    </div>
                    <div className="metric-content">
                      <span className="metric-label">{config.label}</span>
                      <span className="metric-value">{config.format(value)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className="usage-panel budget-panel">
              <div className="usage-panel-header">
                <h3>
                  <Bell size={18} />
                  Credit Budget Alerts
                </h3>
                <span>{budgetAlerts?.period_key || 'Current month'} UTC</span>
              </div>

              {userSettings && userSettings.notification_email === false && (
                <div className="budget-warning">
                  <span>Email notifications are disabled. Budget alerts will be skipped.</span>
                  <button onClick={enableEmailNotifications}>Enable Email</button>
                </div>
              )}

              <div className="budget-grid">
                <div className="budget-field">
                  <label>Monthly budget</label>
                  <input
                    type="number"
                    min={0}
                    step={1}
                    value={budgetMonthlyCredits}
                    placeholder="Disabled"
                    onChange={(event) => {
                      setBudgetMonthlyCredits(event.target.value);
                      setBudgetDirty(true);
                    }}
                  />
                </div>
                <div className={`budget-field ${budgetingDisabled ? 'disabled' : ''}`}>
                  <label>Alert thresholds</label>
                  <div className="budget-chips">
                    {budgetAlertPercents.length === 0 && <span className="budget-empty">No thresholds</span>}
                    {budgetAlertPercents.map((percent) => (
                      <button
                        key={percent}
                        type="button"
                        className="budget-chip"
                        disabled={budgetingDisabled}
                        onClick={() => {
                          setBudgetAlertPercents((prev) => prev.filter((item) => item !== percent));
                          setBudgetDirty(true);
                        }}
                      >
                        {percent}% <span>×</span>
                      </button>
                    ))}
                  </div>
                  <div className="budget-add-row">
                    <input
                      type="number"
                      min={1}
                      max={100}
                      step={1}
                      value={budgetNewPercent}
                      placeholder="Percent"
                      disabled={budgetingDisabled}
                      onChange={(event) => setBudgetNewPercent(event.target.value)}
                    />
                    <button
                      type="button"
                      disabled={budgetingDisabled}
                      onClick={() => {
                        const parsed = parseInt(budgetNewPercent, 10);
                        if (!Number.isFinite(parsed) || parsed < 1 || parsed > 100) return;
                        addBudgetThreshold(parsed);
                        setBudgetNewPercent('');
                      }}
                    >
                      Add
                    </button>
                    {[50, 75, 90, 100].map((percent) => (
                      <button
                        key={percent}
                        type="button"
                        disabled={budgetingDisabled}
                        onClick={() => addBudgetThreshold(percent)}
                      >
                        {percent}%
                      </button>
                    ))}
                  </div>
                </div>
                <div className="budget-status">
                  <div><span>Budget</span><strong>{budgetAlerts?.monthly_budget_mt ? formatCredits(budgetAlerts.monthly_budget_mt) : 'Disabled'}</strong></div>
                  <div><span>Used</span><strong>{formatCredits(budgetAlerts?.consumed_mt || 0)}</strong></div>
                  <div><span>Percent</span><strong>{budgetAlerts?.consumed_percent !== null && budgetAlerts?.consumed_percent !== undefined ? `${budgetAlerts.consumed_percent.toFixed(1)}%` : '-'}</strong></div>
                </div>
              </div>
              <div className="budget-actions">
                <button onClick={saveBudgetAlerts} disabled={budgetSaving || !budgetDirty}>
                  {budgetSaving ? 'Saving...' : 'Save Alerts'}
                </button>
                {budgetError && <span className="budget-error">{budgetError}</span>}
                {budgetSuccess && <span className="budget-success">{budgetSuccess}</span>}
              </div>
            </section>

            <section className="usage-panel">
              <div className="usage-panel-header">
                <h3>
                  <Calendar size={18} />
                  Usage History
                </h3>
                <CustomSelect
                  value={historyMonths.toString()}
                  onChange={handleHistoryMonthsChange}
                  options={[
                    { value: '3', label: 'Last 3 Months' },
                    { value: '6', label: 'Last 6 Months' },
                    { value: '12', label: 'Last 12 Months' },
                  ]}
                />
              </div>
              <div className="usage-table-wrap">
                <table className="usage-table">
                  <thead>
                    <tr>
                      <th>Period</th>
                      <th>Media</th>
                      <th>Minutes</th>
                      <th>Searches</th>
                      <th>Segments</th>
                      <th>Storage Added</th>
                    </tr>
                  </thead>
                  <tbody>
                    {usageHistory?.periods?.map((period) => {
                      const metrics = period.metrics || {};
                      return (
                        <tr key={period.summary_id || period.period_start}>
                          <td>{formatDate(period.period_start)}</td>
                          <td>{formatInteger(Number(metrics.videos_processed || metrics.videos_uploaded || 0))}</td>
                          <td>{formatMinutes(Number(metrics.video_minutes || 0))}</td>
                          <td>{formatInteger(Number(metrics.search_queries || 0) + Number(metrics.image_search_queries || 0))}</td>
                          <td>{formatInteger(Number(metrics.segments_created || 0))}</td>
                          <td>{formatStorage(Number(metrics.storage_bytes || 0))}</td>
                        </tr>
                      );
                    })}
                    {(!usageHistory?.periods || usageHistory.periods.length === 0) && (
                      <tr>
                        <td colSpan={6} className="empty-table">No historical data available</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>

            {isEnterprise && (
              <>
                <section className="usage-panel enterprise-panel">
                  <div className="usage-panel-header">
                    <h3>
                      <Cpu size={18} />
                      Enterprise Token Metrics
                    </h3>
                    <span>Full transparency</span>
                  </div>
                  <div className="metric-grid compact">
                    {['llm_input_tokens', 'llm_output_tokens'].map((metricKey) => {
                      const config = getMetricConfig(metricKey);
                      const value = Number(currentMetrics[metricKey] || 0);
                      return (
                        <div key={metricKey} className="metric-card">
                          <div className="metric-icon" style={{ backgroundColor: `${config.color}20`, color: config.color }}>
                            {config.icon}
                          </div>
                          <div className="metric-content">
                            <span className="metric-label">{config.label}</span>
                            <span className="metric-value">{config.format(value)}</span>
                          </div>
                        </div>
                      );
                    })}
                    <div className="metric-card">
                      <div className="metric-icon" style={{ backgroundColor: 'rgba(99, 102, 241, 0.14)', color: '#6366f1' }}>
                        <Cpu size={20} />
                      </div>
                      <div className="metric-content">
                        <span className="metric-label">Total Tokens</span>
                        <span className="metric-value">{formatCompact(totalTokens)}</span>
                      </div>
                    </div>
                  </div>
                </section>

                {currentUsage?.auth_usage && Object.keys(currentUsage.auth_usage).length > 0 && (
                  <section className="usage-panel">
                    <div className="usage-panel-header">
                      <h3>
                        <Key size={18} />
                        Usage by Auth Source
                      </h3>
                    </div>
                    <div className="auth-grid">
                      {Object.entries(currentUsage.auth_usage).map(([authId, usage]: [string, AuthUsage]) => {
                        const isWebApp = authId === 'web_app';
                        return (
                          <div key={authId} className="auth-card">
                            <div className="auth-card-title">
                              {isWebApp ? <Globe size={16} /> : <Key size={16} />}
                              <span>{usage.name || (isWebApp ? 'Web App' : authId)}</span>
                            </div>
                            <div className="auth-card-stats">
                              <div><span>Input</span><strong>{formatCompact(Number(usage.llm_input_tokens || 0))}</strong></div>
                              <div><span>Output</span><strong>{formatCompact(Number(usage.llm_output_tokens || 0))}</strong></div>
                              <div><span>Minutes</span><strong>{formatMinutes(Number(usage.video_minutes || 0))}</strong></div>
                              <div><span>Searches</span><strong>{formatInteger(Number(usage.search_queries || 0))}</strong></div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </section>
                )}

                <section className="usage-panel">
                  <div className="usage-panel-header events-heading">
                    <h3>
                      <Activity size={18} />
                      Usage Events
                      <span className="event-count">{filteredEvents.length}</span>
                    </h3>
                    <div className="events-controls">
                      <div className="search-input-wrapper">
                        <Search size={16} />
                        <input
                          type="text"
                          placeholder="Search events..."
                          value={eventSearchTerm}
                          onChange={(event) => setEventSearchTerm(event.target.value)}
                        />
                      </div>
                      <CustomSelect value={eventMetricFilter} onChange={setEventMetricFilter} options={eventMetricOptions} />
                      <CustomSelect
                        value={eventSortBy}
                        onChange={(value) => setEventSortBy(value as SortBy)}
                        options={[
                          { value: 'timestamp', label: 'Time' },
                          { value: 'value', label: 'Value' },
                          { value: 'metric_type', label: 'Type' },
                        ]}
                      />
                      <button
                        className="sort-order-btn"
                        onClick={() => setEventSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'))}
                        title={eventSortOrder === 'asc' ? 'Ascending' : 'Descending'}
                      >
                        {eventSortOrder === 'asc' ? '↑' : '↓'}
                      </button>
                    </div>
                  </div>
                  <div className="usage-table-wrap events">
                    <table className="usage-table">
                      <thead>
                        <tr>
                          <th>Timestamp</th>
                          <th>Metric</th>
                          <th>Value</th>
                          <th>Unit</th>
                          <th>Details</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredEvents.map((event) => {
                          const config = getMetricConfig(event.metric_type);
                          return (
                            <tr key={event.event_id}>
                              <td>{formatDateTime(event.timestamp)}</td>
                              <td>
                                <span className="metric-cell-icon" style={{ color: config.color }}>{config.icon}</span>
                                {config.label}
                              </td>
                              <td>{config.format(Number(event.value || 0))}</td>
                              <td>{event.unit}</td>
                              <td className="metadata-cell">
                                {event.metadata && Object.keys(event.metadata).length > 0
                                  ? Object.entries(event.metadata).slice(0, 2).map(([key, value]) => (
                                    <span key={key} className="metadata-tag">
                                      {key}: {typeof value === 'string' ? value.slice(0, 24) : String(value)}
                                    </span>
                                  ))
                                  : '-'}
                              </td>
                            </tr>
                          );
                        })}
                        {filteredEvents.length === 0 && (
                          <tr>
                            <td colSpan={5} className="empty-table">No events found for the selected criteria</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </section>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UsageTab;
